/**
 * PageHero (M10.5 refinement · shared editorial primitive).
 *
 * Every major surface opens with this. Each page passes its own "personality"
 * variant which subtly changes the accent copy — the operator immediately
 * knows where they are without reading the URL.
 *
 * Preserves every route, feature, and data flow — this component is
 * composition-only.
 */
import type { ReactNode } from "react";

export type HeroModule =
  | "command-center"    // Executive briefing
  | "studio"            // Creative workspace
  | "library"           // Digital asset manager
  | "media"             // Film production studio
  | "launch"            // Mission control
  | "publishing"        // Broadcast center
  | "brand-brain"       // Company memory
  | "intelligence"      // Advisor
  | "mission-control"   // Ops room
  | "content-ops"       // Approval
  | "billing";          // Trust

const PERSONALITIES: Record<HeroModule, { eyebrow: string; whisper: string }> = {
  "command-center":  { eyebrow: "Executive Cockpit", whisper: "One glance. Every signal that matters, today." },
  "studio":          { eyebrow: "Creative Workspace", whisper: "Direct the AI. Ship the brief." },
  "library":         { eyebrow: "Digital Asset Manager", whisper: "Every asset your brand has ever produced." },
  "media":            { eyebrow: "Film Production Studio", whisper: "Storyboards, providers, and render — orchestrated." },
  "launch":          { eyebrow: "Launch Control", whisper: "Ten pillars. One decision. Are we ready?" },
  "publishing":      { eyebrow: "Broadcast Center", whisper: "Eight channels. One queue. Zero surprises." },
  "brand-brain":     { eyebrow: "Company Memory", whisper: "The living intelligence behind every generation." },
  "intelligence":    { eyebrow: "Executive Advisor", whisper: "What changed. What matters. What's next." },
  "mission-control": { eyebrow: "Ops Room", whisper: "Every campaign, every lane, every publish." },
  "content-ops":     { eyebrow: "Approvals", whisper: "Human judgement on AI output." },
  "billing":         { eyebrow: "Trust & Billing", whisper: "Stripe managed. Operator controlled." },
};

interface PageHeroProps {
  module: HeroModule;
  title: string | ReactNode;
  subtitle?: string;
  right?: ReactNode;
  liveDot?: boolean;
  eyebrowExtra?: string;
}

export function PageHero({
  module, title, subtitle, right, liveDot = false, eyebrowExtra,
}: PageHeroProps): JSX.Element {
  const p = PERSONALITIES[module];
  return (
    <header
      data-testid={`page-hero-${module}`}
      className="relative overflow-hidden border-b border-[color:var(--md-line)] px-6 py-14 md:px-10 md:py-20 md-anim-fade-in"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 400px at 10% -10%, var(--ws-glow), transparent 60%), radial-gradient(600px 380px at 92% 4%, var(--md-amber-glow), transparent 55%)",
        }}
      />
      <div className="mx-auto flex max-w-[1400px] flex-col gap-10 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <div className={`md-eyebrow md-eyebrow--amber ${liveDot ? "md-eyebrow--live" : ""}`}>
            {p.eyebrow}{eyebrowExtra ? ` · ${eyebrowExtra}` : ""}
          </div>
          <h1 className="md-h1 mt-5">
            {title}
          </h1>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[var(--md-fog)]">
            {subtitle || p.whisper}
          </p>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
    </header>
  );
}
